"use client";

import { useEffect, useRef } from "react";

export function WaveformBackground() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let disposed = false;
    let cleanup = () => {};

    (async () => {
      const THREE = await import("three");
      if (disposed || !container) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      const scene = new THREE.Scene();

      const camera = new THREE.PerspectiveCamera(52, width / height, 0.1, 100);
      camera.position.set(0, 1.6, 4.6);
      camera.lookAt(0, 0.05, 0);

      // Some devices (older mobile, software-rendering Chrome, disabled
      // WebGL flag) throw on WebGLRenderer instantiation. The background is
      // decorative — skip silently rather than letting the error propagate
      // to PostHog $exception tracking and pollute the signal.
      let renderer: InstanceType<typeof THREE.WebGLRenderer>;
      try {
        renderer = new THREE.WebGLRenderer({
          alpha: true,
          antialias: true,
          powerPreference: "low-power",
        });
      } catch {
        return;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
      renderer.setSize(width, height);
      renderer.setClearColor(0x000000, 0);
      container.appendChild(renderer.domElement);

      // Build a grid of horizontal-only line segments. No vertical bars,
      // so the result reads as flowing audio waveform ribbons rather than
      // a topographic grid.
      const NUM_LINES = 44;
      const SEGMENTS = 96;
      const WIDTH = 20;
      const HEIGHT = 11;

      const positions: number[] = [];
      const indices: number[] = [];
      let vIdx = 0;
      for (let l = 0; l < NUM_LINES; l++) {
        const y = (l / (NUM_LINES - 1) - 0.5) * HEIGHT;
        for (let s = 0; s <= SEGMENTS; s++) {
          const x = (s / SEGMENTS - 0.5) * WIDTH;
          positions.push(x, y, 0);
          if (s < SEGMENTS) {
            indices.push(vIdx, vIdx + 1);
          }
          vIdx++;
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      geometry.setIndex(indices);

      const material = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
          uColorWarm: { value: new THREE.Color(0xf59e0b) },
          uColorCool: { value: new THREE.Color(0x6b3d04) },
        },
        vertexShader: /* glsl */ `
          uniform float uTime;
          varying float vIntensity;
          varying float vDepth;

          void main() {
            vec3 p = position;
            float t = uTime;
            float row = (p.y + 5.5) * 0.32;

            float w1 = sin(p.x * 0.42 + t * 0.85 + row * 1.15) * 0.55;
            float w2 = sin(p.x * 0.95 + t * 0.55 - row * 0.7) * 0.28;
            float w3 = cos(p.x * 1.7 - t * 0.35 + row * 0.4) * 0.14;
            float swell = sin(t * 0.25 + row * 0.6) * 0.12;

            p.z = w1 + w2 + w3 + swell;

            vDepth = p.z;
            vIntensity = clamp((p.z + 1.0) * 0.5, 0.0, 1.0);

            gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 uColorWarm;
          uniform vec3 uColorCool;
          varying float vIntensity;
          varying float vDepth;

          void main() {
            float warmth = smoothstep(0.15, 0.95, vIntensity);
            vec3 color = mix(uColorCool, uColorWarm, warmth);
            float alpha = smoothstep(-0.1, 0.85, vIntensity) * 0.42;
            gl_FragColor = vec4(color, alpha);
          }
        `,
      });

      const lines = new THREE.LineSegments(geometry, material);
      lines.rotation.x = -Math.PI * 0.43;
      lines.position.y = -0.25;
      scene.add(lines);

      const timer = new THREE.Timer();
      let raf = 0;
      let isVisible = true;
      let isTabActive = !document.hidden;

      const tick = (now: number) => {
        timer.update(now);
        material.uniforms.uTime.value = timer.getElapsed();
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      };

      const startLoop = () => {
        if (raf || !isVisible || !isTabActive) return;
        // Reset _currentTime so the next delta isn't a giant jump after a
        // pause; the accumulated _elapsed is preserved.
        timer.reset();
        raf = requestAnimationFrame(tick);
      };
      const stopLoop = () => {
        if (!raf) return;
        cancelAnimationFrame(raf);
        raf = 0;
      };

      const onResize = () => {
        if (!container) return;
        const w = container.clientWidth;
        const h = container.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener("resize", onResize);

      const onVisibility = () => {
        isTabActive = !document.hidden;
        if (isTabActive) startLoop();
        else stopLoop();
      };
      document.addEventListener("visibilitychange", onVisibility);

      const io = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting;
          if (isVisible) startLoop();
          else stopLoop();
        },
        { threshold: 0 },
      );
      io.observe(container);

      startLoop();

      cleanup = () => {
        stopLoop();
        io.disconnect();
        window.removeEventListener("resize", onResize);
        document.removeEventListener("visibilitychange", onVisibility);
        geometry.dispose();
        material.dispose();
        renderer.dispose();
        if (renderer.domElement.parentNode) {
          renderer.domElement.parentNode.removeChild(renderer.domElement);
        }
      };
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-0 overflow-hidden"
      style={{
        maskImage:
          "radial-gradient(ellipse 80% 75% at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 95%)",
        WebkitMaskImage:
          "radial-gradient(ellipse 80% 75% at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 95%)",
      }}
    />
  );
}
